/* ===================================================================
   Site Widget - configurator + LIVE preview + embed code + "send install
   instructions to my web person". WIRED to GET/PUT /api/v1/widget and
   POST /api/v1/widget/send-install (Hopper, 2026-06-17).

   PRESENTATION reskinned 2026-07-03 (Corbato) to an EXACT port of the
   Claude Design prototype (Rhody Reviews.dc.html, desktop 1706-1778,
   mobile 2662-2698): sticky header, 1.7fr/1fr body, Configure card
   (segmented Widget-type + Theme, Minimum-rating + Source selects, four
   toggle rows), Live Preview card, Embed Code card (dark data-keepcolor
   block), and a rail with "Send to my web person" + "How it works".
   The data wiring (config load/save, embed-code generation, send-install)
   is UNCHANGED -- only the markup/classes were swapped. Styles in
   styles/widget.css.

   Three widget TYPES, persisted on the config as `layout`:
     - badge  -> renders as the SINGLE-review type (one featured review)
     - carousel -> auto-advancing / prev-next scroller of recent reviews
     - grid   -> responsive grid of recent reviews
   The live preview renders the chosen type from sample data styled exactly
   like the public widget. The embed snippet points at the real per-business
   widget served from the API origin (/w/widget.js + data-key=embed_key).

   "Send install instructions" emails the embed snippet + step-by-step
   install steps to the client's OWN web developer (transactional, real send
   via Brevo; clean copy/mailto fallback if email is unavailable).

   NOTE on the embed block: showing the owner a copy-paste embed snippet for
   THEIR site is an intended feature (NOT the forbidden "raw dev code" -- that
   rule is about hiding integration webhook URLs / API keys / JSON). It carries
   data-keepcolor="1" so the dark block keeps its true color under dark-mode
   invert, matching the prototype.

   NOTE: no "Powered by Rhody Reviews" badge renders unless the client turns
   it on (powered_by) AND they are Pro; non-Pro always shows the credit.
   =================================================================== */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWidget, useUpdateWidget, useSendInstall } from '../hooks';
import { API_BASE, type WidgetConfig, type WidgetUpdate } from '../lib/api';
import '../styles/widget-preview.css';
import '../styles/widget.css';

/* ---- inline icons (stroke) ---- */
function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" strokeLinecap="round" />
    </svg>
  );
}
function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function ChevIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9aa0a6" strokeWidth={2}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
function GoogleGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="#4285F4" className="wg-source-glyph">
      <path d="M12 11v2.6h4.3c-.2 1.1-1.4 3.2-4.3 3.2a4.8 4.8 0 0 1 0-9.6c1.5 0 2.5.6 3 1.1l2-2A7.5 7.5 0 1 0 12 19.5c4.3 0 7.2-3 7.2-7.3 0-.5 0-.9-.1-1.2z" />
    </svg>
  );
}

/* layout value -> human label. 'badge' is the single-review type. */
const LAYOUTS: { label: string; value: WidgetConfig['layout']; hint: string }[] = [
  { label: 'Single', value: 'badge', hint: 'One featured review' },
  { label: 'Carousel', value: 'carousel', hint: 'Scrolls through recent reviews' },
  { label: 'Grid', value: 'grid', hint: 'A grid of recent reviews' },
];
const THEMES: { label: string; value: WidgetConfig['theme'] }[] = [
  { label: 'Light', value: 'light' },
  { label: 'Dark', value: 'dark' },
];

/* The origin the public widget is served from (the API host). */
const WIDGET_ORIGIN = (API_BASE || window.location.origin).replace(/\/+$/, '');

function embedCode(embedKey: string): string {
  return (
    `<div class="rhody-reviews" data-key="${embedKey}"></div>\n` +
    `<script src="${WIDGET_ORIGIN}/w/widget.js" async></script>`
  );
}

/* Sample reviews for the preview (the live widget pulls the client's real,
   approved reviews; this is only the configurator preview). Avatars use the
   deterministic colored-initials fallback -- no third-party image host, so the
   preview can never show a broken remote image. */
type SampleReview = { author: string; rating: number; text: string; date: string; avatar?: string };
const SAMPLE: SampleReview[] = [
  { author: 'Jamie R.', rating: 5, text: 'Showed up on time, did clean work, and the price was exactly what they quoted. Would hire again in a heartbeat.', date: 'Jun 2026' },
  { author: 'Marcus T.', rating: 5, text: 'Fast, friendly, and they explained everything. Highly recommend.', date: 'Jun 2026' },
  { author: 'Priya S.', rating: 4, text: 'Solid job overall and great communication start to finish.', date: 'May 2026' },
  { author: 'Dell W.', rating: 5, text: 'Best in the area. Already told two neighbors.', date: 'May 2026' },
  { author: 'Chris O.', rating: 5, text: 'Professional from the first call. Very happy.', date: 'Apr 2026' },
];

/* Deterministic Google-style avatar color + initials from the author name, so
   the preview matches the public widget's fallback exactly. */
const AV_COLORS = [
  '#1f3a5f', '#c15a3e', '#3563ff', '#2e7d32', '#6a1b9a',
  '#00838f', '#ad1457', '#5d4037', '#455a64', '#d4a04b',
];
function avInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}
function avColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}

function Avatar({ r }: { r: SampleReview }) {
  const [failed, setFailed] = useState(false);
  if (r.avatar && !failed) {
    return (
      <img
        className="wp-avatar wp-avatar-img"
        src={r.avatar}
        alt={r.author}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span className="wp-avatar wp-avatar-initials" aria-hidden style={{ backgroundColor: avColor(r.author) }}>
      {avInitials(r.author)}
    </span>
  );
}

function Stars({ rating }: { rating: number }) {
  const full = Math.round(rating || 0);
  return (
    <span className="wp-stars" aria-label={`${rating} out of 5`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={'wp-star' + (i < full ? ' wp-star-on' : '')}>
          {'★'}
        </span>
      ))}
    </span>
  );
}

/* The Rhody Reviews brand mark (gold-gradient star on a cobalt tile), inlined
   from public/favicon.svg so the "Powered by" credit is fully self-contained
   and always renders with the correct brand colors -- no dependence on app CSS
   or a remote image. Mirrors the public widget credit. */
function RhodyMark({ size = 16 }: { size?: number }) {
  return (
    <svg
      className="wp-pb-mark"
      width={size}
      height={size}
      viewBox="0 0 100 100"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="wpPbStar" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFDA4B" />
          <stop offset=".55" stopColor="#FBBC04" />
          <stop offset="1" stopColor="#E08600" />
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="92" height="92" rx="23" fill="#3563FF" />
      <path
        d="M50,22.7 L56.6,41 L75.9,41.6 L60.6,53.5 L66.1,72.1 L50,61.2 L33.9,72.1 L39.4,53.5 L24.1,41.6 L43.4,41 Z"
        fill="url(#wpPbStar)"
        stroke="url(#wpPbStar)"
        strokeWidth="5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* The "Powered by Rhody Reviews" credit: brand mark + two-tone wordmark, always
   a link back to the marketing site. Shown on every widget for every customer
   (Steve, 2026-08-06 + 2026-08-07). */
function PoweredBy({ variant }: { variant: 'grid' | 'foot' }) {
  return (
    <a
      className={'wp-pb wp-pb-' + variant}
      href="https://owntheboxmarketing.com/reviews/"
      title="Rhody Reviews by Own The Box Marketing"
      target="_blank"
      rel="noopener noreferrer"
    >
      <RhodyMark size={variant === 'grid' ? 18 : 15} />
      <span className="wp-pb-txt">
        Powered by <span className="wp-pb-word">Rhody <b>Reviews</b></span>
      </span>
    </a>
  );
}

function PreviewHead({ avg, count }: { avg: number; count: number }) {
  return (
    <div className="wp-head">
      <span className="wp-avg">{avg.toFixed(1)}</span>
      <Stars rating={avg} />
      <span className="wp-count">{count} reviews</span>
    </div>
  );
}

function ReviewCard({ r }: { r: SampleReview }) {
  return (
    <div className="wp-card">
      <div className="wp-cardtop">
        <Avatar r={r} />
        <div className="wp-who">
          <span className="wp-author">{r.author}</span>
          <span className="wp-sub">
            <Stars rating={r.rating} />
            <span className="wp-date">{r.date}</span>
          </span>
        </div>
      </div>
      <p className="wp-text">{r.text}</p>
    </div>
  );
}

/* Functional carousel for the dashboard preview: auto-advances when
   auto_rotate is on, with working prev/next + dots. Pauses on hover. Mirrors
   the public widget.js behavior so the demo preview actually scrolls. */
function CarouselPreview({ autoRotate }: { autoRotate: boolean }) {
  const [idx, setIdx] = useState(0);
  const n = SAMPLE.length;
  const hoverRef = useRef(false);

  const go = (i: number) => setIdx((i + n) % n);

  useEffect(() => {
    if (!autoRotate) return;
    const t = setInterval(() => {
      if (!hoverRef.current) setIdx((i) => (i + 1) % n);
    }, 4000);
    return () => clearInterval(t);
  }, [autoRotate, n]);

  return (
    <div
      className="wp-carousel-stage"
      onMouseEnter={() => { hoverRef.current = true; }}
      onMouseLeave={() => { hoverRef.current = false; }}
    >
      <ReviewCard r={SAMPLE[idx]} />
      <div className="wp-nav">
        <button type="button" className="wp-arrow" aria-label="Previous" onClick={() => go(idx - 1)}>{'‹'}</button>
        <span className="wp-dots">
          {SAMPLE.map((_, i) => (
            <button
              key={i}
              type="button"
              className={'wp-dot' + (i === idx ? ' wp-dot-on' : '')}
              aria-label={`Review ${i + 1}`}
              onClick={() => go(i)}
            />
          ))}
        </span>
        <button type="button" className="wp-arrow" aria-label="Next" onClick={() => go(idx + 1)}>{'›'}</button>
      </div>
    </div>
  );
}

/* The grid preview shows a representative number of cards (cols x min(rows,2))
   so the chosen column count is visible without an enormous preview. We cycle
   the SAMPLE reviews to fill the cells. */
function gridSampleCount(cols: number, rows: number, continuous: boolean): number {
  const previewRows = Math.min(rows, 2); // keep the preview compact
  const n = cols * previewRows;
  return continuous ? n + cols : n; // hint "there's more" for continuous
}

function WidgetPreview({ cfg }: { cfg: WidgetConfig }) {
  const showOverall = cfg.show_overall !== false;
  const avg = 4.8;
  const count = 127;
  const layout = cfg.layout === 'badge' ? 'single' : cfg.layout;
  // Mirror the server's tier gate: non-Pro always shows the credit.
  // Always on -- not tier-dependent and not customer-controllable.
  const showPoweredBy = true;
  const cols = cfg.grid_cols ?? 3;
  const rows = cfg.grid_rows ?? 4;
  const continuous = (cfg.grid_scroll ?? 'fixed') === 'continuous';
  const cells = gridSampleCount(cols, rows, continuous);

  return (
    <div className={'wp-widget ' + (cfg.theme === 'dark' ? 'wp-dark' : 'wp-light')}>
      {/* Grid powered-by sits at the TOP (gold star, darker) -- matches public. */}
      {layout === 'grid' && showPoweredBy && (
        <div className="wp-grid-powered">
          <PoweredBy variant="grid" />
        </div>
      )}
      {layout === 'single' && (
        <div className="wp-single">
          {showOverall && <PreviewHead avg={avg} count={count} />}
          <ReviewCard r={SAMPLE[0]} />
        </div>
      )}
      {layout === 'carousel' && (
        <div className="wp-carousel-wrap">
          {showOverall && <PreviewHead avg={avg} count={count} />}
          <CarouselPreview autoRotate={cfg.auto_rotate !== false} />
        </div>
      )}
      {layout === 'grid' && (
        <div className="wp-grid-wrap">
          {showOverall && <PreviewHead avg={avg} count={count} />}
          <div className="wp-grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {Array.from({ length: cells }, (_, i) => (
              <ReviewCard key={i} r={SAMPLE[i % SAMPLE.length]} />
            ))}
          </div>
          {continuous && (
            <p className="wp-grid-more">More reviews load as visitors scroll.</p>
          )}
        </div>
      )}
      {/* Single + carousel keep the credit at the bottom (grid moved it to top). */}
      {layout !== 'grid' && showPoweredBy && (
        <div className="wp-foot">
          <PoweredBy variant="foot" />
        </div>
      )}
    </div>
  );
}

/* ---- Reusable toggle row (prototype wTrack/wKnob) ---- */
function ToggleRow({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="wg-togglerow">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        className={'wg-track' + (checked ? ' is-on' : '')}
        onClick={() => { if (!disabled) onChange(!checked); }}
      >
        <span className="wg-knob" />
      </button>
      <span>{label}</span>
    </div>
  );
}

/* ---- Segmented control (prototype segStyle) ---- */
function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: T; hint?: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="wg-seg">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          title={o.hint}
          className={o.value === value ? 'is-active' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SendInstall({ embedKey }: { embedKey: string }) {
  const sender = useSendInstall();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [result, setResult] = useState<
    { sent: boolean; mailto: string; subject: string; body: string } | null
  >(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const emailOk = /\S+@\S+\.\S+/.test(email);

  async function handleSend() {
    setErr(null);
    setResult(null);
    if (!emailOk) {
      setErr('Enter a valid email address.');
      return;
    }
    try {
      const res = await sender.send({
        to_email: email.trim(),
        to_name: name.trim() || undefined,
        note: note.trim() || undefined,
      });
      setResult({ sent: res.sent, mailto: res.mailto, subject: res.subject, body: res.body });
    } catch {
      setErr('Could not send right now. Please try again.');
    }
  }

  async function copyBody() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked */
    }
  }

  return (
    <div className="wg-card">
      <div className="wg-railtitle">Send to my web person</div>
      <p className="wg-railcopy">
        Not the one who updates the website? Send your developer the snippet plus
        install instructions. We will email it to them for you.
      </p>
      <div className="wg-form">
        <label className="wg-field">
          <span className="wg-lab">THEIR EMAIL</span>
          <input
            className="wg-input"
            type="email"
            placeholder="Your developer's email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="wg-field">
          <span className="wg-lab">THEIR NAME (OPTIONAL)</span>
          <input
            className="wg-input"
            type="text"
            placeholder="Your developer's name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="wg-field">
          <span className="wg-lab">ADD A NOTE (OPTIONAL)</span>
          <textarea
            className="wg-input"
            rows={2}
            placeholder="A short note for them (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="wg-sendbtn"
          disabled={!emailOk || sender.isPending}
          onClick={handleSend}
        >
          <MailIcon /> {sender.isPending ? 'Sending...' : 'Send install instructions'}
        </button>
      </div>
      {err && <p className="wg-err">{err}</p>}
      {result && result.sent && (
        <p className="wg-ok">
          Sent to {email}. They will get the snippet and step-by-step instructions.
        </p>
      )}
      {result && !result.sent && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 12.5, color: '#5f6368', lineHeight: 1.5 }}>
            We could not send the email automatically. Copy the instructions below
            and send them yourself, or open your email app pre-filled.
          </p>
          <div className="wg-secondrow">
            <button type="button" className="wg-ghostbtn" onClick={copyBody}>
              <CopyIcon /> {copied ? 'Copied' : 'Copy instructions'}
            </button>
            <a className="wg-ghostbtn" href={result.mailto}>
              <MailIcon /> Open in email app
            </a>
          </div>
        </div>
      )}
      <p className="wg-note">
        Embed key: <code>{embedKey}</code>
      </p>
    </div>
  );
}

export function Widget() {
  const q = useWidget();
  const update = useUpdateWidget();
  const cfg = q.data?.widget ?? null;
  // The "Powered by Rhody Reviews" credit is ALWAYS shown, for every customer,
  // and is not customer-controllable at any price (Steve, 2026-08-06). The
  // public widget render enforces it server-side; the preview just mirrors it.

  const [copied, setCopied] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  function persist(patch: WidgetUpdate) {
    update.save(patch).then(
      () => {
        setSavedTick(true);
        setTimeout(() => setSavedTick(false), 1500);
      },
      () => {},
    );
  }

  const code = useMemo(() => (cfg ? embedCode(cfg.embed_key) : ''), [cfg]);

  async function handleCopy() {
    if (!cfg) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked; user can select the snippet manually */
    }
  }

  if (!cfg) {
    return (
      <div className="wg">
        <div className="wg-head">
          <h1>Site Widget</h1>
          <p>Configure the review showcase you embed on your site.</p>
        </div>
        <div className="wg-card wg-loadcard">
          <div className="wg-cardtitle">Configure</div>
          <p style={{ fontSize: 13, lineHeight: 1.5, color: '#5f6368' }}>
            {q.isError
              ? 'We could not load your widget settings. Please try again shortly.'
              : 'Loading your widget settings...'}
          </p>
        </div>
      </div>
    );
  }

  const layoutLabel = LAYOUTS.find((l) => l.value === cfg.layout)?.label;

  return (
    <div className="wg">
      {/* ---- Sticky header ---- */}
      <div className="wg-head">
        <h1>Site Widget</h1>
        <p>Configure the review showcase you embed on your site.</p>
      </div>

      {/* ---- Body: 1.7fr / 1fr ---- */}
      <div className="wg-body">
        {/* MAIN column: Configure + Preview + Embed */}
        <div className="wg-main">
          {/* ---- Configure ---- */}
          <div className="wg-card" data-tour="widget-configure">
            <div className="wg-cardtitle">Configure</div>
            <div className="wg-fields">
              <div>
                <div className="wg-lab">WIDGET TYPE</div>
                <Segmented
                  options={LAYOUTS}
                  value={cfg.layout}
                  onChange={(v) => persist({ layout: v })}
                />
              </div>
              <div>
                <div className="wg-lab">THEME</div>
                <Segmented
                  options={THEMES}
                  value={cfg.theme}
                  onChange={(v) => persist({ theme: v })}
                />
              </div>
              <div>
                <div className="wg-lab">MINIMUM RATING</div>
                <div className="wg-select-wrap">
                  <select
                    className="wg-select"
                    value={cfg.min_rating}
                    onChange={(e) => persist({ min_rating: Number(e.target.value) })}
                  >
                    <option value={4}>4 stars &amp; up</option>
                    <option value={5}>5 stars only</option>
                    <option value={1}>All ratings</option>
                  </select>
                  <span className="wg-select-chev"><ChevIcon /></span>
                </div>
              </div>
              <div>
                <div className="wg-lab">SOURCE</div>
                <div className="wg-select-wrap">
                  <select
                    className="wg-select"
                    style={{ paddingLeft: 34 }}
                    value={cfg.source}
                    onChange={(e) => persist({ source: e.target.value as WidgetConfig['source'] })}
                  >
                    <option value="google">Google reviews</option>
                    <option value="all">All platforms</option>
                  </select>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'inline-flex' }}>
                    <GoogleGlyph />
                  </span>
                  <span className="wg-select-chev"><ChevIcon /></span>
                </div>
              </div>

              {cfg.layout === 'grid' && (
                <>
                  <div>
                    <div className="wg-lab">GRID COLUMNS</div>
                    <div className="wg-select-wrap">
                      <select
                        className="wg-select"
                        value={cfg.grid_cols ?? 3}
                        onChange={(e) => persist({ grid_cols: Number(e.target.value) })}
                      >
                        <option value={3}>3 across</option>
                        <option value={4}>4 across</option>
                        <option value={5}>5 across</option>
                      </select>
                      <span className="wg-select-chev"><ChevIcon /></span>
                    </div>
                  </div>
                  <div>
                    <div className="wg-lab">GRID ROWS</div>
                    <div className="wg-select-wrap">
                      <select
                        className="wg-select"
                        value={cfg.grid_rows ?? 4}
                        onChange={(e) => persist({ grid_rows: Number(e.target.value) })}
                      >
                        <option value={4}>4 down</option>
                        <option value={5}>5 down</option>
                        <option value={6}>6 down</option>
                        <option value={7}>7 down</option>
                      </select>
                      <span className="wg-select-chev"><ChevIcon /></span>
                    </div>
                  </div>
                  <div>
                    <div className="wg-lab">HOW MANY REVIEWS</div>
                    <div className="wg-select-wrap">
                      <select
                        className="wg-select"
                        value={cfg.grid_scroll ?? 'fixed'}
                        onChange={(e) => persist({ grid_scroll: e.target.value as WidgetConfig['grid_scroll'] })}
                      >
                        <option value="fixed">Fixed grid (rows x columns)</option>
                        <option value="continuous">Show all (keeps scrolling)</option>
                      </select>
                      <span className="wg-select-chev"><ChevIcon /></span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="wg-toggles">
              <ToggleRow
                label="Show overall rating & count"
                checked={cfg.show_overall}
                onChange={(v) => persist({ show_overall: v })}
              />
              <ToggleRow
                label="Auto-rotate carousel"
                checked={cfg.auto_rotate}
                onChange={(v) => persist({ auto_rotate: v })}
              />
              <ToggleRow
                label="Only verified customers"
                checked={cfg.verified_only}
                onChange={(v) => persist({ verified_only: v })}
              />
              {/* The "Powered by" toggle is GONE (Steve, 2026-08-06: "I want
                  the Powered by badge on every single one"). It was a disabled
                  switch plus an "Upgrade to Pro" upsell -- and there is no Pro
                  to upgrade to. Showing a control that cannot be operated, next
                  to an upsell for a plan that does not exist, is worse than
                  showing nothing. The public widget render enforces this
                  server-side, so this is only a statement of fact. */}
              <p className="wg-togglehint">
                Every widget shows a small &ldquo;Powered by Rhody Reviews&rdquo; credit.
              </p>
            </div>

            {savedTick && (
              <p style={{ fontSize: 12, color: '#188038', marginTop: 12 }}>Saved</p>
            )}
            {update.error && (
              <p className="wg-err">Could not save that change. Please try again.</p>
            )}
          </div>

          {/* ---- Preview (sample numbers, not the owner's real stats) ---- */}
          <div className="wg-card">
            <div className="wg-lab" style={{ marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Preview
              <span className="wg-samplechip">Sample</span>
            </div>
            <div className="wg-previewsub">
              On your site it shows your real approved reviews. The rating and count
              here are sample numbers. Showing the <strong>{layoutLabel}</strong> type.
            </div>
            <div className={'wg-preview' + (cfg.theme === 'dark' ? ' is-dark' : '')}>
              <WidgetPreview cfg={cfg} />
            </div>
          </div>

          {/* ---- Embed code ---- */}
          <div className="wg-card">
            <div className="wg-lab" style={{ marginBottom: 4 }}>EMBED CODE</div>
            <div className="wg-embedsub">
              Paste this where you want the reviews to appear. It loads your reviews
              automatically and stays up to date.
            </div>
            <pre className="wg-code" data-keepcolor="1">{code}</pre>
            <button type="button" className="wg-copybtn" onClick={handleCopy}>
              <CopyIcon /> {copied ? 'Copied' : 'Copy embed code'}
            </button>
          </div>
        </div>

        {/* RAIL: send-to-web-person + how it works */}
        <div className="wg-rail">
          <SendInstall embedKey={cfg.embed_key} />
          <div className="wg-info">
            <h3>How it works</h3>
            <p>
              Choose a widget type, copy the embed code, and paste it into your
              website. The widget pulls your approved reviews automatically.
            </p>
            <p className="muted2">
              Change the type or settings any time. No re-paste needed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
